import React, {useCallback, useMemo, useState} from 'react';

import {TargetBlank} from '../../common/util/links';
import {Button} from '../Button';
import {CodeSnippet} from '../CodeSnippet';
import {useInferenceContext} from '../PagePanelComponents/Home/Browse3/inference/util';
import {useCanAccessInference} from '../PagePanelComponents/Home/Browse3/inference/util';
import {
  getCodeSnippetForProvider,
  getEvaluationSnippetForProvider,
  LLMProvider,
  SnippetType,
} from '../PagePanelComponents/Home/Browse3/pages/common/traceCodeSnippets';
import {Tailwind} from '../Tailwind';
import {ToggleButtonGroup} from '../ToggleButtonGroup';
import {getProviderNoteComponent} from './ProviderNotes';

const PROVIDER_OPTIONS = [
  {value: 'W&B Inference'},
  {value: 'OpenAI'},
  {value: 'Anthropic'},
];

// Map display names to provider keys
const DISPLAY_NAME_TO_PROVIDER: Record<string, LLMProvider> = {
  OpenAI: 'openai',
  Anthropic: 'anthropic',
  'W&B Inference': 'wandb-inference',
};

// Map provider keys to display names
const PROVIDER_TO_DISPLAY_NAME: Record<LLMProvider, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  'wandb-inference': 'W&B Inference',
};

export interface CodeBlockProps {
  /** The type of snippet to display (trace or evaluation) */
  snippetType: SnippetType;
  /** The entity name for the Weave project */
  entity: string;
  /** The project name for the Weave project */
  project: string;
  /** Optional default provider to use. If not specified, will default based on inference access */
  defaultProvider?: LLMProvider;
  /** Whether to show the expand/collapse functionality (useful for long evaluation snippets) */
  showExpandCollapse?: boolean;
  /** Whether to show provider notes (default: false) */
  showProviderNotes?: boolean;
  /** Optional max height for the code snippet (e.g., '532px' or 'calc(100vh - 532px)') */
  maxHeight?: string;
  /** Optional callback for when code is copied, used for analytics */
  onCopy?: (provider: LLMProvider, code: string) => void;
  /** Optional callback for when provider is changed, used for analytics */
  onProviderChange?: (provider: LLMProvider) => void;
}

/**
 * CodeBlock component displays interactive code snippets for getting started with Weave.
 * Supports both trace and evaluation snippets across different LLM providers (OpenAI, Anthropic, W&B Inference).
 *
 * Features:
 * - Automatically filters providers based on user's inference access
 * - Defaults to OpenAI when W&B Inference is not accessible
 * - Provides "Open in Colab" action buttons
 * - Supports expand/collapse for long code snippets
 * - Analytics-ready with copy and provider change callbacks
 */
export const CodeBlock: React.FC<CodeBlockProps> = ({
  snippetType,
  entity,
  project,
  defaultProvider,
  showExpandCollapse = false,
  showProviderNotes = false,
  maxHeight,
  onCopy,
  onProviderChange,
}) => {
  // Check if user has access to W&B Inference
  const inferenceContext = useInferenceContext();
  const {canAccessInference} = useCanAccessInference(inferenceContext);

  // Filter provider options based on inference access
  const availableProviderOptions = useMemo(() => {
    if (canAccessInference) {
      return PROVIDER_OPTIONS;
    }
    // Filter out W&B Inference if user doesn't have access
    return PROVIDER_OPTIONS.filter(opt => opt.value !== 'W&B Inference');
  }, [canAccessInference]);

  // Determine the default provider to use
  const initialProvider = useMemo(() => {
    if (defaultProvider) {
      // If explicitly provided, use it (but only if available)
      if (defaultProvider === 'wandb-inference' && !canAccessInference) {
        return 'openai';
      }
      return defaultProvider;
    }
    // Default to OpenAI if no inference access, otherwise W&B Inference
    return canAccessInference ? 'wandb-inference' : 'openai';
  }, [defaultProvider, canAccessInference]);

  const [selectedDisplayName, setSelectedDisplayName] = useState(
    PROVIDER_TO_DISPLAY_NAME[initialProvider]
  );

  const selectedProvider = DISPLAY_NAME_TO_PROVIDER[selectedDisplayName];

  // Get the appropriate code snippet based on snippet type
  const codeSnippet = useMemo(() => {
    if (snippetType === 'evaluation') {
      return getEvaluationSnippetForProvider(selectedProvider, entity, project);
    }
    return getCodeSnippetForProvider(selectedProvider, entity, project);
  }, [snippetType, selectedProvider, entity, project]);

  const handleCopy = useCallback(() => {
    if (onCopy) {
      onCopy(selectedProvider, codeSnippet);
    }
  }, [onCopy, selectedProvider, codeSnippet]);

  const handleProviderChange = useCallback(
    (displayName: string) => {
      setSelectedDisplayName(displayName);
      const provider = DISPLAY_NAME_TO_PROVIDER[displayName];
      if (onProviderChange) {
        onProviderChange(provider);
      }
    },
    [onProviderChange]
  );

  // Determine Colab URL based on snippet type
  const colabUrl =
    snippetType === 'evaluation'
      ? 'https://wandb.me/weave-quickstart-evals'
      : 'https://wandb.me/weave-quickstart-traces';

  return (
    <Tailwind>
      <div className="mt-16">
        {/* Top row: Provider toggles on left, action buttons on right */}
        <div className="mb-8 flex items-center justify-between">
          <ToggleButtonGroup
            options={availableProviderOptions}
            value={selectedDisplayName}
            size="small"
            onValueChange={handleProviderChange}
          />
          <div className="flex gap-2">
            <TargetBlank href={colabUrl}>
              <Button size="small" variant="ghost" icon="logo-colab">
                Open in Colab
              </Button>
            </TargetBlank>
          </div>
        </div>

        {/* Provider note (optional) */}
        {showProviderNotes && (
          <div className="mb-12 text-moon-500">
            {getProviderNoteComponent(selectedProvider)}
          </div>
        )}

        {/* Code snippet display */}
        <CodeSnippet
          showExpandCollapse={showExpandCollapse}
          maxHeight={maxHeight}
          codeSnippet={codeSnippet}
          analyticsEvent={handleCopy}
          analyticsDefaultProperties={{
            provider: selectedProvider,
            entity,
            project,
            snippetType,
          }}
        />
      </div>
    </Tailwind>
  );
};
